import { action, computed, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import {
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { PersonalOrganizerPrefillQuestion } from '@/features/legends/questions/questions'
import { OrganizerBindingQuestionEditorViewModelBase } from './OrganizerBindingQuestionEditorViewModelBase'

const elementName = 'Personal Organizer Prefill'

/**
 * Prefills a personal tax organizer field through a prefill binding. Nothing
 * can reference it, so it has no ID, and only relevant elements send a
 * prefill.
 */
export class PersonalOrganizerPrefillQuestionEditorViewModel extends OrganizerBindingQuestionEditorViewModelBase<PersonalOrganizerPrefillQuestion> {
  private readonly _schema: IValidationSchema<PersonalOrganizerPrefillQuestionEditorViewModel> =
    {
      triggerWhen: QuestionEditorViewModelBase.triggerWhenValidators,
      prefillBindingId: this._bindingIdValidators('prefill'),
      expr: [func(() => this.expr.analyze(), 'There are formula errors.')],
    }

  readonly type = 'PersonalOrganizerPrefill'

  @observable accessor prefillBindingId = ''

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options, true)
    this._validateWhileEditing(this._schema, {
      prefillBindingId: ['expr'],
    })
  }

  /** @inheritDoc */
  get bindingId() {
    return this.prefillBindingId
  }

  /** @inheritDoc */
  @action.bound
  setBindingId(id: string) {
    this.prefillBindingId = id
  }

  /** @inheritDoc */
  @computed
  get catalog() {
    return this.dependencies.symbols.organizerBindings?.personal.prefill ?? []
  }

  /**
   * Computed from the binding, so it isn't edited or saved.
   */
  @computed
  get label() {
    return this.binding
      ? `${this.binding.displayName} (${elementName})`
      : elementName
  }

  /** @inheritDoc */
  get displayText() {
    return this.label
  }

  /** @inheritDoc */
  @action
  hydrate(question: PersonalOrganizerPrefillQuestion) {
    this._hydrateShared(question)
    this.prefillBindingId = question.prefillBindingId ?? ''
    this.expr.setValue(question.expr)
  }

  /** @inheritDoc */
  protected _serialize(): PersonalOrganizerPrefillQuestion {
    return {
      type: this.type,
      triggerWhen: this.triggerWhen.toJSON(),
      prefillBindingId: this.prefillBindingId,
      expr: this.expr.toJSON(),
    }
  }
}
